import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, CircularProgress, Grid } from '@mui/material'

import {
  useGET_studentLearningStyleQuestionsQuery,
  usePOST_studentLearningStyleMutation,
} from 'redux/api/studentsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import LearningStyleList from 'components/utilized/student/LearningStyleList'
import LearningStyleResult from 'components/utilized/student/LearningStyleResult'
import LearningStyleStepper from 'components/utilized/student/LearningStyleStepper'

import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import { useSuccessAlert } from 'utils/customHooks/useSuccessAlert'
import formModel, { initialValues, validationSchema } from 'utils/formModels/learningStyleModel'
import { crudColorMap } from 'utils/helpers'
import { useMedia } from 'utils/customHooks/useMedia'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'

const StudentLearningStyleForm: FC = () => {
  const { t } = useTranslation()
  const { answers } = formModel()
  const [activeIndex, setActiveIndex] = useState(0)

  const user = useSelector((state: RootState) => state['user'].user)
  const gradeLevel = user?.student_profile?.grade_level ?? null

  const {
    isLoading: entityLoading,
    data: entity,
    error: entityError,
  } = useGET_studentLearningStyleQuestionsQuery()
  const [
    addEntity,
    { isLoading: createLoading, isSuccess: createSuccess, error: createError, data: addResponseData },
  ] = usePOST_studentLearningStyleMutation()

  const isLoading = createLoading || entityLoading
  const error = createError || entityError

  useErrorAlert(error)
  useSuccessAlert(createSuccess ? t('alerts.message-sent') : '')

  const { isUnder900px } = useMedia()


  const quizData =
    entity && user && 
    entity?.questions?.filter(question => {

      if( 
       (question.student_type == "small" && gradeLevel <= 2) ||
       (question.student_type == "big" && gradeLevel > 2 )
      )return true
      else return false

    }).map((question, i) => {
      return {
        number: i + 1,
        question_id: question.id,
        text: question.text,
        answer_options: question.answer_options,
        answer: question.selected_answer_option,
      }
    })
  const serverValues = quizData
    ? {
        answers: quizData,
      }
    : null

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={serverValues || initialValues}
        validationSchema={validationSchema}
        onSubmit={(values: any) => {
          const answersData = values?.answers.map((question) => {
            return {
              id: question.question_id,
              selected_answer_option: question.answer,
            }
          })
          addEntity({ questions: answersData })
        }}
      >
        {({ values, setFieldValue, resetForm }) => {
          return (
            <Form
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
              }}
            >
              <Grid
                container
                spacing={2}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: '1',
                }}
              >
                {addResponseData || entity?.is_taken ? (
                  <LearningStyleResult data={addResponseData || entity} />
                ) : (
                  <>

                    {!isUnder900px && <LearningStyleStepper activeStep={activeIndex} total={quizData.length} />}

                    {values?.answers.length > 0 && (
                      <LearningStyleList fieldProps={{ ...answers }} activeIndex={activeIndex} />
                    )}
                    <Grid item xs={6} sx={{ textAlign: 'start' }}>
                      <Button
                        variant='contained'
                        color={'success'}
                        sx={{ fontWeight: '500', minHeight: '2.5rem' }}
                        onClick={() => setActiveIndex((prevIndex) => prevIndex - 1)}
                        disableElevation
                        disabled={activeIndex === 0}
                      >
                        {isLoading || values?.answers.length <= 0 ? (
                          <CircularProgress variant='indeterminate' size={20} sx={{ color: '#000' }} />
                        ) : (
                          <>{`${t('common.back')}`}</>
                        )}
                      </Button>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'end' }}>
                      {activeIndex + 1 < values?.answers.length ? (
                        <Button
                          variant='contained'
                          color={'success'}
                          sx={{ fontWeight: '500', minHeight: '2.5rem' }}
                          onClick={() => setActiveIndex((prevIndex) => prevIndex + 1)}
                          disableElevation
                        >
                          {isLoading || values?.answers.length <= 0 ? (
                            <CircularProgress variant='indeterminate' size={20} sx={{ color: '#000' }} />
                          ) : (
                            <>{`${t('common.next')}`}</>
                          )}
                        </Button>
                      ) : (
                        <SubmitButton
                          color={crudColorMap['add']}
                          text={`${t('common.save')}`}
                          sx={{ fontWeight: '500', minHeight: '2.5rem', width: '120px' }}
                          isLoading={isLoading}
                        />
                      )}
                    </Grid>
                  </>
                )}
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default StudentLearningStyleForm
